import { BOTONES } from '../../../../../componentes/botones';
import {
  Component,
  ElementRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import {
  HojaExportLuminaria,
  IExportLuminariasConfig,
} from '../../../../../auxiliares/servicios/http/luminarias.service';
import { VueloDescargasService } from '../../../../navigation/descargas/vuelo-descargas.service';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { InfoBannerComponent } from '../../../../../componentes/info-banner/info-banner.component';

interface IOpcionHoja {
  key: HojaExportLuminaria;
  label: string;
  descripcion: string;
  /** Trae el mes de histórico: hace el export bastante más lento. */
  temporal?: boolean;
  /** Sale desmarcada (el usuario la activa si la quiere). */
  offPorDefecto?: boolean;
}

interface IGrupoColumnas {
  hoja: HojaExportLuminaria;
  key: string;
  label: string;
  /** Fuera del default: grupos muy anchos que el usuario activa a mano. */
  offPorDefecto?: boolean;
}

// Versionada: cuando cambian las hojas/grupos por defecto, una preferencia
// guardada con los defaults viejos los arrastra (v2 traía Eventos y Comandos
// marcados). Bumpear la clave descarta la preferencia previa.
const STORAGE_KEY = 'exportLuminariasConfig.v3';

/**
 * Diálogo donde el usuario elige QUÉ ver en el Excel de luminarias: qué hojas,
 * qué grupos de columnas (en las hojas que los tienen). La ventana de las hojas
 * históricas es fija (último mes) y la decide el backend. La última selección se
 * persiste en `localStorage`.
 */
@Component({
  selector: 'app-exportar-luminarias-dialog',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    BOTONES,
    MatIconModule,
    CardHeaderComponent,
    InfoBannerComponent,
  ],
  template: `
    <mat-card>
      <app-card-header
        [title]="'Exportar luminarias a Excel'"
        icono="download"
        [botonCerrar]="true"
        (cerrar)="cerrar()"
      ></app-card-header>

      <mat-card-content class="card-content-crear exp-lum">
        <p class="exp-lum-ayuda">
          Elegí qué hojas y columnas incluir. La exportación respeta los filtros
          activos de la tabla.
        </p>

        <div class="exp-lum-hojas">
          @for (op of opciones; track op.key) {
            <div class="exp-lum-hoja">
              <mat-checkbox
                [(ngModel)]="seleccion[op.key]"
                [disabled]="op.key === 'inventario'"
              >
                <span class="exp-lum-hoja-titulo">{{ op.label }}</span>
                <span class="exp-lum-hoja-desc">{{ op.descripcion }}</span>
              </mat-checkbox>

              <!-- Grupos de columnas de la hoja -->
              @if (seleccion[op.key] && gruposDe(op.key).length) {
                <div class="exp-lum-grupos">
                  @for (g of gruposDe(op.key); track g.key) {
                    <mat-checkbox [(ngModel)]="gruposSel[op.key + ':' + g.key]">
                      {{ g.label }}
                    </mat-checkbox>
                  }
                </div>
              }
            </div>
          }
        </div>

        <p class="exp-lum-ayuda">
          Las hojas históricas (alarmas, eventos, comandos) traen siempre el
          <strong>último mes completo</strong>
          .
        </p>

        @if (hojasLentas().length) {
          <app-info-banner
            icon="schedule"
            color="orange"
            [closable]="false"
            [title]="avisoDemora()"
          ></app-info-banner>
        }
      </mat-card-content>

      <mat-card-actions class="actions-create">
        <button irxBoton="secundario" type="button" (click)="cerrar()">
          Cancelar
        </button>
        <button irxBoton="principal" type="button" (click)="exportar()">
          <mat-icon>file_download</mat-icon>
          Exportar
        </button>
      </mat-card-actions>
    </mat-card>
  `,
  styles: [
    `
      .exp-lum .exp-lum-ayuda {
        margin: 12px 0 0;
        font-size: 12px;
        color: var(--muted-text);
      }
      .exp-lum .exp-lum-hojas {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding-top: 8px;
      }
      .exp-lum .exp-lum-hoja-titulo {
        font-size: 14px;
        font-weight: 500;
        color: var(--text-color);
      }
      .exp-lum .exp-lum-hoja-desc {
        display: block;
        font-size: 12px;
        line-height: 1.25;
        white-space: normal;
        color: var(--muted-text);
      }
      .exp-lum .exp-lum-grupos {
        display: flex;
        flex-wrap: wrap;
        gap: 0 16px;
        margin: 0 0 6px 32px;
      }
      .exp-lum app-info-banner {
        display: block;
        margin-top: 12px;
      }
    `,
  ],
})
export class ExportarLuminariasDialogComponent {
  private ref = inject(MatDialogRef<ExportarLuminariasDialogComponent>);
  private elemento = inject<ElementRef<HTMLElement>>(ElementRef);
  private vuelo = inject(VueloDescargasService);
  public data = inject<{ nivel?: string }>(MAT_DIALOG_DATA, {
    optional: true,
  });

  public opciones: IOpcionHoja[] = [
    {
      key: 'inventario',
      label: 'Inventario',
      descripcion: 'Identidad, modelo y márgenes eléctricos (obligatoria).',
    },
    {
      key: 'estado',
      label: 'Estado al momento del informe',
      descripcion:
        'Foto del momento: estado (6 colores), encendido, alarmas activas, modo y conectividad.',
    },
    {
      key: 'historico',
      label: 'Histórico de alarmas',
      descripcion: 'Alarmas por día y conteo por tipo, del último mes.',
      temporal: true,
    },
    {
      key: 'electrico',
      label: 'Eléctrico',
      descripcion: 'V, I, W, FP, kWh, dimming.',
    },
    {
      key: 'eventos',
      label: 'Eventos (histórico)',
      descripcion: 'Coloreados por categoría, del último mes.',
      temporal: true,
      offPorDefecto: true,
    },
    {
      key: 'comandos',
      label: 'Comandos enviados',
      descripcion:
        'Downlinks del último mes: estado (aplicado por ACK/uplink, no recibido…), payload, origen y usuario.',
      temporal: true,
      offPorDefecto: true,
    },
    {
      key: 'puestas',
      label: 'Puestas',
      descripcion: 'Puestas del scope y su cantidad de luminarias.',
    },
    {
      key: 'grupos',
      label: 'Grupos',
      descripcion: 'Grupos de luminarias / puestas.',
    },
    {
      key: 'trazabilidad',
      label: 'Trazabilidad',
      descripcion: 'Historial de asignaciones (nodo, puesta, grupo, perfil).',
    },
  ];

  public grupos: IGrupoColumnas[] = [
    { hoja: 'inventario', key: 'identidad', label: 'Identidad/ubicación' },
    { hoja: 'inventario', key: 'modelo', label: 'Modelo' },
    { hoja: 'inventario', key: 'margenes', label: 'Márgenes eléctricos' },
    { hoja: 'inventario', key: 'dimerizacion', label: 'Curva de dimerización' },
    { hoja: 'estado', key: 'alarmas', label: 'Alarmas activas' },
    { hoja: 'estado', key: 'operacion', label: 'Modo/relé/día-noche' },
    { hoja: 'estado', key: 'conectividad', label: 'Conectividad/señal' },
  ];

  public seleccion: Partial<Record<HojaExportLuminaria, boolean>> = {};
  public gruposSel: Record<string, boolean> = {};

  constructor() {
    this.restaurar();
  }

  public gruposDe(hoja: HojaExportLuminaria): IGrupoColumnas[] {
    return this.grupos.filter((g) => g.hoja === hoja);
  }

  /**
   * Hojas marcadas que traen el mes de histórico. Son las que hacen lento el
   * export (la flota entera son ~80k eventos y ~324k comandos en el mes), así
   * que se avisa antes de generar.
   */
  public hojasLentas(): string[] {
    return this.opciones
      .filter((o) => o.temporal && this.seleccion[o.key])
      .map((o) => o.label);
  }

  /** Aviso de demora cuando hay hojas con el mes de histórico. */
  public avisoDemora(): string {
    return (
      `<strong>${this.hojasLentas().join(' y ')}</strong> traen el mes ` +
      'completo, así que este export puede demorar varios minutos. Se genera ' +
      'en segundo plano: podés seguir trabajando y el archivo aparece en ' +
      '<strong>Descargas</strong> cuando está listo.'
    );
  }

  public cerrar(): void {
    this.ref.close();
  }

  public exportar(): void {
    const hojas = this.opciones
      .map((o) => o.key)
      .filter((k) => this.seleccion[k]);
    if (!hojas.includes('inventario')) hojas.unshift('inventario');

    const gruposColumnas: IExportLuminariasConfig['gruposColumnas'] = {};
    for (const g of this.grupos) {
      if (this.seleccion[g.hoja] && this.gruposSel[g.hoja + ':' + g.key]) {
        (gruposColumnas[g.hoja] ??= []).push(g.key);
      }
    }

    // Sin rango: la ventana de las hojas históricas la fija el backend (último
    // mes). El usuario no la elige — un rango a mano era la principal fuente de
    // exports que morían por timeout.
    const config: IExportLuminariasConfig = { hojas, gruposColumnas };

    this.persistir(config);

    // El diálogo "vuela" hasta el ícono de Descargas (análogo al minimizado de
    // tratamiento): deja claro a dónde fue el export, que se genera aparte.
    const panel =
      this.elemento.nativeElement.closest<HTMLElement>('.cdk-overlay-pane');
    const espera = this.vuelo.volarPanel(panel);
    if (!espera) {
      this.ref.close(config);
      return;
    }
    setTimeout(() => this.ref.close(config), espera);
  }

  // ── Persistencia de la última selección ──

  private restaurar(): void {
    // Default: todo marcado salvo eventos (temporal); todos los grupos on.
    for (const o of this.opciones)
      this.seleccion[o.key] = !o.temporal && !o.offPorDefecto;
    for (const g of this.grupos)
      this.gruposSel[g.hoja + ':' + g.key] = !g.offPorDefecto;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const cfg = JSON.parse(raw) as IExportLuminariasConfig;
      if (cfg.hojas?.length) {
        for (const o of this.opciones)
          this.seleccion[o.key] = cfg.hojas.includes(o.key);
        this.seleccion['inventario'] = true;
      }
      if (cfg.gruposColumnas) {
        for (const g of this.grupos)
          this.gruposSel[g.hoja + ':' + g.key] = !!cfg.gruposColumnas[
            g.hoja
          ]?.includes(g.key);
      }
    } catch {
      // selección por defecto
    }
  }

  private persistir(config: IExportLuminariasConfig): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          hojas: config.hojas,
          gruposColumnas: config.gruposColumnas,
        }),
      );
    } catch {
      // ignorar cuota/localStorage no disponible
    }
  }
}
